import { identifyBrazeUser, startBrazeIfConsented, trackBrazeEvent } from './useBraze';
import type { SubscriptionState } from './index';
import type { Account } from '@/models/account/authModels';

/**
 * Map user communication preferences to Braze subscription states
 */
export function mapPreferencesToBrazeSubscriptions(account: Account): {
    email: SubscriptionState;
    push: SubscriptionState;
} {
    // User is opted in to email if ANY of these are true
    const emailOptedIn = account.receiveDonatosNewsAndEventEmails || account.ltdCommunicationEmail || account.lrmEmails || account.receivePersonalizedOffers || account.isMarketingEmail;

    // User is opted in to push/SMS if ANY of these are true
    const pushOptedIn = account.receiveSmsOffers || account.receiveMobileAppOffers;

    return {
        email: emailOptedIn ? 'OPTED_IN' : 'UNSUBSCRIBED',
        push: pushOptedIn ? 'OPTED_IN' : 'UNSUBSCRIBED',
    };
}

/**
 * Initialize Braze SDK on app launch
 */
export async function initializeBraze(): Promise<void> {
    // For now, assume consent is granted
    // TODO: Integrate with actual consent management (?)
    const hasConsent = true;

    await startBrazeIfConsented(hasConsent);
}

/**
 * Sync user profile to Braze after login or profile update
 */
export async function syncUserToBraze(account: Account): Promise<void> {
    if (!account.customerId || !account.emailAddress) {
        console.warn('Braze: Cannot sync user without customerId and emailAddress');
        return;
    }

    const subscriptions = mapPreferencesToBrazeSubscriptions(account);

    await identifyBrazeUser(account.customerId, account.emailAddress, subscriptions);
}

/**
 * Track order placement event
 */
export async function trackOrderPlaced(orderId: string, total: number, storeNumber?: string): Promise<void> {
    await trackBrazeEvent('order_placed', {
        order_id: orderId,
        total,
        store: storeNumber || 'Unknown',
    });
}

/**
 * Track user registration event
 */
export async function trackUserRegistered(customerId: string, email: string): Promise<void> {
    await trackBrazeEvent('user_registered', {
        customer_id: customerId,
        email,
    });
}

/**
 * Track communication preference updates
 */
export async function trackPreferenceUpdate(preferenceType: string, optedIn: boolean): Promise<void> {
    await trackBrazeEvent('preference_updated', {
        preference_type: preferenceType,
        opted_in: optedIn,
    });
}
